import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
    Loader2, Upload, Plus, Search, Trash2, Minus, Coins, Download,
    Copy, Link2, KeyRound, CheckCircle2, AlertCircle, Tags, X, Users,
} from 'lucide-react';
import { canEdit as canEditSection } from '@/lib/auth';
import { ADMIN_STUDENTS } from '@/constants/testIds';

function copyToClipboard(text, label = 'Copied') {
    navigator.clipboard.writeText(text);
    toast.success(label);
}

/* Multi-select toggle chips for mentor categories (domains). */
function CategoryChips({ all, value, onChange }) {
    const toggle = (d) => {
        const set = new Set(value);
        set.has(d) ? set.delete(d) : set.add(d);
        onChange(Array.from(set));
    };
    if (!all || all.length === 0) {
        return (
            <p className="text-xs text-muted-foreground">
                No mentor categories yet. Set a <strong>Domain</strong> on mentors (Admin → Mentors) to create categories.
            </p>
        );
    }
    return (
        <div className="flex flex-wrap gap-2" data-testid="category-chips">
            {all.map((d) => {
                const active = value.includes(d);
                return (
                    <button
                        key={d}
                        type="button"
                        onClick={() => toggle(d)}
                        data-testid={`category-chip-${d}`}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            active
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card text-foreground hover:bg-accent/40'
                        }`}
                    >
                        {d}
                    </button>
                );
            })}
        </div>
    );
}

function MagicLinkChip({ student, onRegenerate }) {
    if (student.has_password) {
        return (
            <Badge variant="secondary" className="gap-1">
                <CheckCircle2 className="h-3 w-3 text-accent" />
                Password set
            </Badge>
        );
    }
    return (
        <div className="flex items-center gap-1">
            <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300">
                <AlertCircle className="h-3 w-3" />
                Pending setup
            </Badge>
            {student.magic_link && (
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1.5"
                    onClick={() => copyToClipboard(student.magic_link, 'Magic link copied')}
                    title="Copy magic link"
                >
                    <Copy className="h-3 w-3" />
                </Button>
            )}
            <Button
                size="sm"
                variant="ghost"
                className="h-6 px-1.5"
                onClick={() => onRegenerate(student)}
                title="Regenerate magic link"
            >
                <KeyRound className="h-3 w-3" />
            </Button>
        </div>
    );
}

function AdjustCreditsDialog({ student, onDone }) {
    const [delta, setDelta] = useState(0);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        setLoading(true);
        try {
            await api.patch(`/admin/students/${student.id}/credits`, { delta: Number(delta), reason });
            toast.success('Credits updated');
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={!!student} onOpenChange={(o) => !o && onDone()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-display">Adjust credits</DialogTitle>
                </DialogHeader>
                {student && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-border/60 bg-secondary p-3 text-sm">
                            <div><strong>{student.name}</strong> — {student.email}</div>
                            <div>Current credits: <strong>{student.credits}</strong></div>
                        </div>
                        <div>
                            <Label>Change (use negative to remove)</Label>
                            <div className="mt-1 flex items-center gap-2">
                                <Button size="icon" variant="secondary" onClick={() => setDelta((v) => Number(v) - 1)}><Minus className="h-4 w-4" /></Button>
                                <Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} className="text-center" />
                                <Button size="icon" variant="secondary" onClick={() => setDelta((v) => Number(v) + 1)}><Plus className="h-4 w-4" /></Button>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">New balance: {Math.max((student.credits || 0) + Number(delta), 0)}</div>
                        </div>
                        <div>
                            <Label>Reason (optional)</Label>
                            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Bonus credit" />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={onDone}>Cancel</Button>
                    <Button onClick={submit} disabled={loading || !delta}>
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddStudentDialog({ open, onOpenChange, onCreated, categories }) {
    const [form, setForm] = useState({ name: '', email: '', phone: '', college_id: '', credits: 1, allowed_domains: [] });
    const [loading, setLoading] = useState(false);
    const [created, setCreated] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post('/admin/students', { ...form, credits: Number(form.credits) });
            setCreated(data);
            toast.success('Student added — magic link ready');
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        onOpenChange(false);
        setForm({ name: '', email: '', phone: '', college_id: '', credits: 1, allowed_domains: [] });
        setCreated(null);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display">Add a student</DialogTitle>
                </DialogHeader>
                {created ? (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-accent/30 bg-secondary p-4 text-sm">
                            <div className="flex items-center gap-2 font-semibold text-primary">
                                <CheckCircle2 className="h-4 w-4 text-accent" />
                                Student added
                            </div>
                            <div className="mt-1">
                                <strong>{created.name}</strong> · {created.email} · {created.college_id}
                            </div>
                        </div>
                        <div>
                            <Label>Magic link (share with the student)</Label>
                            <div className="mt-1 flex gap-2">
                                <Input readOnly value={created.magic_link || ''} className="font-mono text-xs" />
                                <Button onClick={() => copyToClipboard(created.magic_link, 'Magic link copied')}>
                                    <Copy className="mr-1 h-4 w-4" /> Copy
                                </Button>
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground">
                                Share via email, SMS or WhatsApp. The student uses this link to set their password.
                                Link is valid for 14 days.
                            </p>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <Label>Name</Label>
                            <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Email</Label>
                                <Input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>College ID</Label>
                                <Input required value={form.college_id} onChange={(e) => setForm((f) => ({ ...f, college_id: e.target.value }))} />
                            </div>
                            <div>
                                <Label>Starting credits</Label>
                                <Input type="number" min="0" value={form.credits} onChange={(e) => setForm((f) => ({ ...f, credits: e.target.value }))} />
                            </div>
                        </div>
                        <div>
                            <Label>Visible mentor categories <span className="text-muted-foreground">(optional)</span></Label>
                            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
                                Choose which mentor domains this student can see. Leave empty = can see <strong>all</strong> mentors.
                            </p>
                            <CategoryChips all={categories} value={form.allowed_domains} onChange={(v) => setForm((f) => ({ ...f, allowed_domains: v }))} />
                        </div>
                    </form>
                )}
                <DialogFooter>
                    {created ? (
                        <Button onClick={close}>Done</Button>
                    ) : (
                        <>
                            <Button variant="ghost" type="button" onClick={close}>Cancel</Button>
                            <Button onClick={submit} disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Add & generate magic link
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UploadDialog({ open, onOpenChange, onDone }) {
    const [file, setFile] = useState(null);
    const [defaultCredits, setDefaultCredits] = useState(1);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const upload = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/admin/students/upload', fd, {
                params: { default_credits: defaultCredits },
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setResult(data);
            toast.success(`Created ${data.created}, updated ${data.updated}`);
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed');
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        onOpenChange(false);
        setFile(null);
        setResult(null);
    };

    const copyAllLinks = () => {
        if (!result?.created_students?.length) return;
        const text = result.created_students
            .map((s) => `${s.name} <${s.email}>\n  Magic link: ${s.magic_link}`)
            .join('\n\n');
        navigator.clipboard.writeText(text);
        toast.success('All magic links copied');
    };

    const sampleCsv = 'name,email,phone,college_id,credits,domains\nAsha Rao,asha@college.edu,+91-9876543210,CS2024001,2,Finance;Consulting\nRohit Iyer,rohit@college.edu,,CS2024002,1,Consulting\nMeera Nair,meera@college.edu,,CS2024003,1,\n';
    const downloadSample = () => {
        const blob = new Blob([sampleCsv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'students-sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="max-h-[92vh] overflow-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">Upload students (CSV)</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="rounded-lg border border-border/60 bg-secondary p-3 text-sm">
                        CSV columns: <code className="rounded bg-background px-1">name, email, phone, college_id, credits, domains</code><br />
                        <span className="text-muted-foreground">
                            <strong>domains</strong> sets which mentor categories a student can see — separate multiple with <code className="rounded bg-background px-1">;</code> (e.g. <code className="rounded bg-background px-1">Finance;Consulting</code>). Leave blank = can see all mentors.
                        </span><br />
                        Each new student is created <strong>without a password</strong> — they get a magic link (below) to set their own.
                        <Button variant="ghost" size="sm" className="mt-2" onClick={downloadSample}>
                            <Download className="mr-1 h-4 w-4" /> Download sample CSV
                        </Button>
                    </div>
                    <div>
                        <Label>File</Label>
                        <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </div>
                    <div>
                        <Label>Default credits (if not in CSV)</Label>
                        <Input type="number" min="0" value={defaultCredits} onChange={(e) => setDefaultCredits(Number(e.target.value))} />
                    </div>
                    {result && (
                        <div className="space-y-3">
                            <div className="rounded-lg border border-border/60 bg-muted/40 p-3 text-sm">
                                <div>Created: <strong>{result.created}</strong> · Updated: <strong>{result.updated}</strong></div>
                                {result.errors?.length > 0 && (
                                    <div className="mt-2 text-destructive">
                                        <strong>{result.errors.length} issues:</strong>
                                        <ul className="mt-1 list-disc pl-5 text-xs">
                                            {result.errors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            {result.created_students?.length > 0 && (
                                <div className="rounded-lg border border-accent/30 bg-secondary p-3">
                                    <div className="mb-2 flex items-center justify-between">
                                        <div className="font-semibold text-primary">Magic links for {result.created_students.length} new student(s)</div>
                                        <Button size="sm" variant="secondary" onClick={copyAllLinks}>
                                            <Copy className="mr-1 h-4 w-4" /> Copy all
                                        </Button>
                                    </div>
                                    <div className="space-y-2 max-h-64 overflow-auto">
                                        {result.created_students.map((s, i) => (
                                            <div key={i} className="rounded border border-border/60 bg-background p-2 text-xs">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-medium">{s.name}</div>
                                                        <div className="text-muted-foreground">{s.email} · {s.college_id} · {s.credits} credit(s)</div>
                                                    </div>
                                                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(s.magic_link, `Link copied for ${s.name}`)}>
                                                        <Copy className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                                <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground">{s.magic_link}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="mt-2 text-xs text-muted-foreground">
                                        Email delivery is not yet configured on this instance. Share these links via your own channel (email/SMS/WhatsApp).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={close}>Close</Button>
                    <Button disabled={!file || loading} onClick={upload}>
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                        Upload
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditCategoriesDialog({ student, categories, onDone }) {
    const [value, setValue] = useState(student?.allowed_domains || []);
    const [loading, setLoading] = useState(false);
    useEffect(() => { setValue(student?.allowed_domains || []); }, [student]);

    const save = async () => {
        setLoading(true);
        try {
            await api.patch(`/admin/students/${student.id}`, { allowed_domains: value });
            toast.success('Categories updated');
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={!!student} onOpenChange={(o) => !o && onDone()}>
            <DialogContent data-testid="edit-categories-dialog">
                <DialogHeader><DialogTitle className="font-display">Mentor categories</DialogTitle></DialogHeader>
                {student && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-border/60 bg-secondary p-3 text-sm">
                            <strong>{student.name}</strong> — {student.email}
                        </div>
                        <div>
                            <Label>Visible mentor categories</Label>
                            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
                                Select which mentor domains this student can see. Leave empty = can see <strong>all</strong> mentors.
                            </p>
                            <CategoryChips all={categories} value={value} onChange={setValue} />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={onDone}>Cancel</Button>
                    <Button onClick={save} disabled={loading} data-testid="edit-categories-save">
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkCategoriesDialog({ open, count, studentIds, categories, onDone, onClose }) {
    const [value, setValue] = useState([]);
    const [mode, setMode] = useState('set');
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) { setValue([]); setMode('set'); } }, [open]);

    const apply = async () => {
        setLoading(true);
        try {
            const { data } = await api.post('/admin/students/bulk-domains', {
                student_ids: studentIds,
                allowed_domains: value,
                mode,
            });
            toast.success(`Updated ${data.updated} student(s)`);
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally {
            setLoading(false);
        }
    };

    const modes = [
        { key: 'set', label: 'Replace' },
        { key: 'add', label: 'Add to existing' },
        { key: 'clear', label: 'Clear all' },
    ];

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="bulk-categories-dialog">
                <DialogHeader><DialogTitle className="font-display">Set categories for {count} student(s)</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Action</Label>
                        <div className="mt-1 flex flex-wrap gap-2">
                            {modes.map((m) => (
                                <button
                                    key={m.key}
                                    type="button"
                                    onClick={() => setMode(m.key)}
                                    data-testid={`bulk-mode-${m.key}`}
                                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                                        mode === m.key ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-accent/40'
                                    }`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {mode !== 'clear' ? (
                        <div>
                            <Label>Categories</Label>
                            <div className="mt-1"><CategoryChips all={categories} value={value} onChange={setValue} /></div>
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            This removes all category restrictions — the selected students will be able to see <strong>all</strong> mentors.
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button onClick={apply} disabled={loading || (mode !== 'clear' && value.length === 0)} data-testid="bulk-categories-apply">
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Apply
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminStudentsPage() {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [adjust, setAdjust] = useState(null);
    const [categories, setCategories] = useState([]);
    const [editCats, setEditCats] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const canEdit = canEditSection('students');

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/students', { params: q ? { q } : undefined });
            setStudents(data.students || []);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const { data } = await api.get('/admin/mentor-domains');
            setCategories(data.domains || []);
        } catch {
            setCategories([]);
        }
    };

    useEffect(() => { refresh(); loadCategories(); /* eslint-disable-next-line */ }, []);

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const allVisibleSelected = students.length > 0 && students.every((s) => selected.has(s.id));
    const toggleSelectAll = () => {
        setSelected((prev) => {
            if (students.every((s) => prev.has(s.id))) return new Set();
            return new Set(students.map((s) => s.id));
        });
    };
    const clearSelection = () => setSelected(new Set());

    const del = async (s) => {
        if (!window.confirm(`Delete ${s.name}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/students/${s.id}`);
            toast.success('Deleted');
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    const regenerateMagic = async (s) => {
        try {
            const { data } = await api.post(`/admin/students/${s.id}/magic-link`);
            copyToClipboard(data.magic_link, `Fresh magic link copied for ${s.name}`);
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <AdminLayout
            title="Students"
            subtitle="Manage college students, credits, and password invites"
            actions={canEdit && (
                <>
                    <Button variant="secondary" onClick={() => setUploadOpen(true)} data-testid={ADMIN_STUDENTS.upload_button}>
                        <Upload className="mr-2 h-4 w-4" /> Upload CSV
                    </Button>
                    <Button onClick={() => setAddOpen(true)} data-testid={ADMIN_STUDENTS.add_button}>
                        <Plus className="mr-2 h-4 w-4" /> Add student
                    </Button>
                </>
            )}
        >
            <Card className="mb-4">
                <CardContent className="flex flex-col gap-3 p-4 sm:flex-row">
                    <div className="relative flex-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            className="pl-9"
                            placeholder="Search by name, email, or college ID…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && refresh()}
                        />
                    </div>
                    <Button variant="secondary" onClick={refresh}>Search</Button>
                </CardContent>
            </Card>

            {canEdit && selected.size > 0 && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3" data-testid="students-bulk-toolbar">
                    <span className="flex items-center gap-2 text-sm font-medium text-primary">
                        <Users className="h-4 w-4" /> {selected.size} student{selected.size > 1 ? 's' : ''} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="students-bulk-categories">
                            <Tags className="mr-1.5 h-4 w-4" /> Set mentor categories
                        </Button>
                        <Button size="sm" variant="ghost" onClick={clearSelection}>
                            <X className="mr-1 h-4 w-4" /> Clear
                        </Button>
                    </div>
                </div>
            )}

            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                    ) : students.length === 0 ? (
                        <div className="p-10 text-center text-sm text-muted-foreground">No students yet. Upload a CSV or add manually to get started.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        {canEdit && (
                                            <TableHead className="w-10">
                                                <Checkbox
                                                    checked={allVisibleSelected}
                                                    onCheckedChange={toggleSelectAll}
                                                    aria-label="Select all"
                                                    data-testid="students-select-all"
                                                />
                                            </TableHead>
                                        )}
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>College ID</TableHead>
                                        <TableHead>Credits</TableHead>
                                        <TableHead>Mentor categories</TableHead>
                                        <TableHead>Access</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {students.map((s) => (
                                        <TableRow key={s.id} data-state={selected.has(s.id) ? 'selected' : undefined}>
                                            {canEdit && (
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selected.has(s.id)}
                                                        onCheckedChange={() => toggleSelect(s.id)}
                                                        aria-label={`Select ${s.name}`}
                                                        data-testid={`student-select-${s.id}`}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell className="text-sm font-medium">{s.name}</TableCell>
                                            <TableCell className="text-sm">{s.email}</TableCell>
                                            <TableCell className="text-sm tabular-nums">{s.college_id}</TableCell>
                                            <TableCell>
                                                <Badge className="gap-1 bg-accent/20 text-primary hover:bg-accent/20"><Coins className="h-3 w-3" /> {s.credits}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {(!s.allowed_domains || s.allowed_domains.length === 0) ? (
                                                    <span className="text-xs text-muted-foreground">All mentors</span>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1" data-testid={`student-categories-${s.id}`}>
                                                        {s.allowed_domains.map((d) => (
                                                            <Badge key={d} variant="secondary" className="font-normal">{d}</Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <MagicLinkChip student={s} onRegenerate={regenerateMagic} />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {canEdit && (
                                                    <div className="flex justify-end gap-1">
                                                        <Button size="sm" variant="ghost" onClick={() => setEditCats(s)} data-testid={`student-edit-categories-${s.id}`} title="Edit mentor categories">
                                                            <Tags className="h-4 w-4" />
                                                        </Button>
                                                        <Button size="sm" variant="secondary" onClick={() => setAdjust(s)} data-testid={ADMIN_STUDENTS.adjust_credits}>
                                                            Adjust
                                                        </Button>
                                                        <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => del(s)} data-testid={ADMIN_STUDENTS.delete}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <AddStudentDialog open={addOpen} onOpenChange={setAddOpen} onCreated={refresh} categories={categories} />
            <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={() => { refresh(); loadCategories(); }} />
            {adjust && <AdjustCreditsDialog student={adjust} onDone={() => { setAdjust(null); refresh(); }} />}
            {editCats && <EditCategoriesDialog student={editCats} categories={categories} onDone={() => { setEditCats(null); refresh(); }} />}
            <BulkCategoriesDialog
                open={bulkOpen}
                count={selected.size}
                studentIds={Array.from(selected)}
                categories={categories}
                onClose={() => setBulkOpen(false)}
                onDone={() => { setBulkOpen(false); clearSelection(); refresh(); }}
            />
        </AdminLayout>
    );
}
